// apps/landing-page/src/components/search/useSearchBox.ts
// Query, rows and keyboard model for a search input with a result list. Used by the
// navbar combobox and the dialog; the owner decides where Escape and Tab go.
'use client';

import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import type { CatalogSearch } from './useCatalogSearch';
import { searchPageHref, seeAllRow, type SearchRow, type Translate } from './searchRows';

export function useSearchBox(search: CatalogSearch, listId: string, onDone: () => void) {
  const t = useTranslations('search');
  const router = useRouter();
  const [query, setQueryState] = useState('');
  // -1 = nothing active. Only an arrow key (or the pointer) activates a row.
  const [activeIndex, setActiveIndex] = useState(-1);

  const trimmed = query.trim();
  const { rowsFor, quickRows } = search;
  const rows = useMemo<SearchRow[]>(() => {
    if (!trimmed) return quickRows;
    const results = rowsFor(trimmed);
    return results.length > 0 ? [...results, seeAllRow(trimmed, t as Translate)] : results;
  }, [trimmed, rowsFor, quickRows, t]);

  const activeRow = rows[activeIndex] as SearchRow | undefined;
  const activeDomId = activeRow ? `${listId}-${activeRow.id}` : undefined;

  useEffect(() => {
    if (activeDomId) document.getElementById(activeDomId)?.scrollIntoView({ block: 'nearest' });
  }, [activeDomId]);

  const setQuery = (value: string) => {
    setQueryState(value);
    setActiveIndex(-1);
  };

  const go = (href: string) => {
    onDone();
    router.push(href);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' && rows.length > 0) {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % rows.length);
    } else if (e.key === 'ArrowUp' && rows.length > 0) {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? rows.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      if (activeRow) {
        e.preventDefault();
        go(activeRow.href);
      } else if (trimmed) {
        e.preventDefault();
        go(searchPageHref(trimmed));
      }
    }
  };

  return { query, setQuery, trimmed, rows, activeDomId, setActiveIndex, onKeyDown };
}

export type SearchBox = ReturnType<typeof useSearchBox>;
